<template>
  <div id="menu-list">
    <Breadcrumbs
      :crumbs="[
        { name: getGlobals.info.school_name, link: `${getPathPrefix}/` },
        { name: getSectionName, link: `${getPathPrefix}/dining` },
      ]"
    />
    <LoadingIndicator v-show="loading" />
    <div
      v-show="!loading"
      ref="container"
      class="page-container"
    >
      <div
        v-if="getDiningPageFilters && getDiningPageFilters.length > 1"
        class="filter"
      >
        <select
          v-model="selectedFilter"
          name="filter"
          :aria-label="$t('pages.dining.diningFilter')"
          data-testid="dining-filter-dropdown"
          @change="filterCallback"
        >
          <option
            v-for="filter in getDiningPageFilters"
            :key="filter.id"
            :data-testid="`dining-filter-option-${filter.id}`"
            :value="filter.id"
          >
            {{ filter.name.toUpperCase() }}
          </option>
        </select>
      </div>
      <menu-item
        v-for="item in menus"
        :key="item.id"
        data-testid="dining-menu-item"
        :item="item"
      />
    </div>
    <div class="dining-legal page-container">
      <div
        data-testid="dining-disclaimer"
        class="page-container-item"
        v-html="getDiningPageDisclaimer.fda_statement"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, useStore } from 'vuex';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';
import Breadcrumbs from '../../components/global/Breadcrumbs.vue';
import LoadingIndicator from '../../components/global/LoadingIndicator.vue';
import MenuItem from '../../components/pages/dining/menu-item';
import pageHelpers from '../page-mixins';
import getLocale from '../../helpers/locale';
import { fetchFiltersAndDisclaimer, fetchMenus, formatDate } from '../../helpers/section/dining.async';

export default defineNuxtComponent({
  name: 'Menus',
  components: {
    MenuItem,
    Breadcrumbs,
    LoadingIndicator,
  },
  mixins: [pageHelpers],
  beforeRouteUpdate(to, from, next) {
    if (import.meta.client && to.query.filter !== from.query.filter) {
      this.fetchFiltersAndMenus(to.query, document.cookie);
      next();
    } else {
      next();
    }
  },
  async setup() {
    const store = useStore();
    const { data } = await useAsyncData('dining', () =>
      fetchFiltersAndDisclaimer({ store, pageQuery: useRoute().query, api: useNuxtApp().$axios }),
    );
    useHead({
      title: store.getters.getSectionName,
    });
    return data.value;
  },
  data() {
    return {
      loading: false,
      selectedFilter: null,
      menus: [],
      scrollTop: 0,
      loadAdditionalMenu: false,
    };
  },
  computed: {
    ...mapGetters([
      'getDiningApi',
      'getLinks',
      'getGlobals',
      'getPathPrefix',
      'getCustomSections',
      'getSectionName',
      'getDiningPageFilters',
      'getDiningPageDisclaimer',
    ]),
    ...mapGetters('translation', ['getLanguage']),
  },
  async mounted() {
    document.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    if (import.meta.client) {
      document.removeEventListener('scroll', this.handleScroll);
    }
  },
  methods: {
    formatDate,
    /* used for SSG only */
    async fetchFiltersAndMenus(query, cookie) {
      this.loading = true;
      const langCode = getLocale(this.$store, cookie);
      if (!isEmpty(langCode) && langCode !== 'en') {
        const { menus, selectedFilter, links } = await fetchFiltersAndDisclaimer({
          store: this.$store,
          pageQuery: query,
          api: this.$axios,
        });
        this.selectedFilter = selectedFilter;
        this.menus = menus;
        this.links = links;
        this.loading = false;
        return;
      }
      if (!isEmpty(query)) {
        const { menus, selectedFilter, links } = await fetchMenus({
          store: this.$store,
          pageQuery: query,
          api: this.$axios,
        });
        this.selectedFilter = selectedFilter;
        this.menus = menus;
        this.links = links;
        this.loading = false;
      }
      this.loading = false;
    },
    handleScroll() {
      this.scrollTop = window.pageYOffset;
      const container = this.$refs.container;
      const { clientHeight } = document.documentElement;
      if (this.scrollTop + clientHeight > container.clientHeight) {
        this.addMenuResults();
      }
    },
    async addMenuResults() {
      if (!this.links.next) return;
      try {
        if (this.loadAdditionalMenu) return;
        this.loadAdditionalMenu = true;
        const url = new URL(this.links.next);
        const newPath = url.pathname.concat(url.search);
        const { data } = await this.$axios.get(newPath);
        this.loadAdditionalMenu = false;
        const nextMenuPage = data.menus;
        this.links = data.meta.links;
        this.menus = concat(this.menus, nextMenuPage);
      } catch (e) {
        console.error(e);
      }
    },
  },
});
</script>

<style scoped lang="scss">
#menu-list {
  .menu-filter {
    float: right;
  }
  .menus {
    max-width: 100%;
    max-height: 300px;
    overflow: hidden;
    border-bottom: 1px solid #ddd;
  }
  .page-container {
    padding-bottom: 0;
  }
  .dining-legal {
    line-height: 17px;
    color: #555;
    padding-bottom: 30px;
    .page-container-item {
      border-bottom: none;
    }
    :deep() {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin-bottom: 15px;
        font-weight: 700;
        line-height: 1;
        color: var(--heading-color);
      }
      a {
        color: var(--links-color);
      }
      p {
        margin-bottom: 15px;
        font-weight: 400;
        font-size: 13px;
      }
      li,
      blockquote,
      pre {
        font-size: 13px;
      }
    }
  }
}
</style>
